import type { CollectionSummaryJSONObject } from './collection.summary';

/**
 * Variant layers are derived on demand from the collections in a job; nothing about them is stored.
 * A "layer" groups the collections that share a variant name (PoleOS's `variantName`, carried on
 * Office as `variantTypeName`), mirroring how PoleOS identifies a design set. The "Existing" layer is
 * the base poles collected in Office (collections with no `variantType`). The tree is reconstructed by
 * following each variant's `variantForUuid` back to the collection it derives from: a variant whose
 * parent is a base pole sits under Existing; if a variant ever derives from another variant it nests
 * deeper, so the derivation is depth-capable even though PoleOS is 2-level today.
 */

/** Sentinel id of the "Existing" (base) layer, which has no `variantName`. */
export const EXISTING_LAYER_ID = 'existing';

/** The collection fields the layer derivation needs. */
export type VariantLayerCollection = Pick<
	CollectionSummaryJSONObject,
	'uuid' | 'revision' | 'variantType' | 'variantTypeName' | 'variantForUuid'
>;

/** A node in the derived variant-layer tree. */
export interface VariantLayerNode {
	/** Layer id: the variant name (`variantTypeName`), or {@link EXISTING_LAYER_ID} for Existing. */
	id: string;
	/** Display label. Empty for Existing (the UI substitutes its own translated label). */
	name: string;
	/** Variant type of this layer (e.g. 'proposed'); undefined for Existing. */
	variantType?: string;
	/** Revisions of the collections that belong to this layer. */
	revisions: string[];
	/** Child layers (design sets derived from this layer). */
	children: VariantLayerNode[];
}

/** The layer id a collection belongs to: its variant name, or Existing when it is not a variant. */
export function variantLayerId(collection: Pick<VariantLayerCollection, 'variantType' | 'variantTypeName'>): string {
	if (collection.variantType === undefined) {
		return EXISTING_LAYER_ID;
	}
	return collection.variantTypeName ?? collection.variantType;
}

interface LayerAccumulator {
	id: string;
	name: string;
	variantType?: string;
	revisions: string[];
	members: VariantLayerCollection[];
}

/**
 * Derive the variant-layer tree for a job from its collections. Returns the Existing layer as the
 * root, with design-set layers nested beneath the layer they derive from. Children are ordered by
 * name for a stable display. The Existing root is always present, even when the job has only
 * Verify-created poles (in which case it holds no revisions).
 */
export function deriveVariantLayerTree(collections: readonly VariantLayerCollection[]): VariantLayerNode {
	const byUuid = new Map<string, VariantLayerCollection>();
	for (const collection of collections) {
		byUuid.set(collection.uuid, collection);
	}

	const layers = new Map<string, LayerAccumulator>();
	const layerFor = (id: string): LayerAccumulator => {
		let layer = layers.get(id);
		if (layer === undefined) {
			layer = { id, name: id === EXISTING_LAYER_ID ? '' : id, revisions: [], members: [] };
			layers.set(id, layer);
		}
		return layer;
	};
	// The Existing root always exists.
	layerFor(EXISTING_LAYER_ID);

	for (const collection of collections) {
		const id = variantLayerId(collection);
		const layer = layerFor(id);
		layer.revisions.push(collection.revision);
		layer.members.push(collection);
		if (id !== EXISTING_LAYER_ID) {
			layer.name = collection.variantTypeName ?? collection.variantType ?? id;
			layer.variantType = collection.variantType;
		}
	}

	// Parent of each layer = the layer of the collections its members derive from (Existing by default).
	const parentId = new Map<string, string>();
	layers.forEach((layer, id) => {
		if (id === EXISTING_LAYER_ID) {
			return;
		}
		let resolved = EXISTING_LAYER_ID;
		for (const member of layer.members) {
			if (member.variantForUuid === undefined) {
				continue;
			}
			const parent = byUuid.get(member.variantForUuid);
			if (parent === undefined) {
				continue;
			}
			const candidate = variantLayerId(parent);
			if (candidate !== id) {
				resolved = candidate;
				break;
			}
		}
		parentId.set(id, resolved);
	});

	const nodes = new Map<string, VariantLayerNode>();
	layers.forEach((layer, id) => {
		nodes.set(id, { id, name: layer.name, variantType: layer.variantType, revisions: layer.revisions, children: [] });
	});

	const root = nodes.get(EXISTING_LAYER_ID)!;
	nodes.forEach((node, id) => {
		if (id === EXISTING_LAYER_ID) {
			return;
		}
		const parent = nodes.get(parentId.get(id) ?? EXISTING_LAYER_ID) ?? root;
		parent.children.push(node);
	});

	sortChildren(root);
	return root;
}

function sortChildren(node: VariantLayerNode): void {
	node.children.sort((a, b) => a.name.localeCompare(b.name));
	for (const child of node.children) {
		sortChildren(child);
	}
}
